import { useEffect } from "react";
import {
  Control,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { useDebouncedValue } from "./useDebouncedValue";
import { useCepQuery } from "./useCepQuery";
import { STATE_ABBREVIATION_MAP } from "@shared/constants";

interface UseAutoFillCepParams<T extends FieldValues> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
}

export const useAutoFillCep = <T extends FieldValues>({
  control,
  setValue,
}: UseAutoFillCepParams<T>) => {
  const cepValue =
    (useWatch({ control, name: "address.zipCode" as Path<T> }) as string) ??
    "";
  const debouncedCep = useDebouncedValue(cepValue, 500);

  const { data, isLoading, isFetching } = useCepQuery(debouncedCep);

  useEffect(() => {
    if (data && !data.erro) {
      if (data.logradouro)
        setValue(
          "address.street" as Path<T>,
          data.logradouro as PathValue<T, Path<T>>,
        );
      if (data.bairro)
        setValue(
          "address.neighborhood" as Path<T>,
          data.bairro as PathValue<T, Path<T>>,
        );
      if (data.localidade)
        setValue(
          "address.city" as Path<T>,
          data.localidade as PathValue<T, Path<T>>,
        );
      if (data.uf)
        setValue(
          "address.state" as Path<T>,
          (STATE_ABBREVIATION_MAP[data.uf] ?? "") as PathValue<T, Path<T>>,
        );
      setValue(
        "address.country" as Path<T>,
        "Brasil" as PathValue<T, Path<T>>,
      );
    }
  }, [data, setValue]);

  return { isLoading: isLoading || isFetching };
};
